import { arrayIncludesAny, filterDuplicates } from '@nu-art/ts-common';
import { DiseasePathwayTypes } from './types.js';
const isDiseasePathway = (type) => !!type && DiseasePathwayTypes.includes(type);
const getPathwayTagValues = (tagGroups) => filterDuplicates((tagGroups ?? []).flatMap(group => group.value));
/**
 * Resolves the organization tag ids a pathway belongs to, mirroring runtime org scoping:
 * a pathway belongs to a context rule if any of its tag values match the rule (arrayIncludesAny),
 * and we keep only the org tags referenced by those matching rules.
 */
const getPathwayOrgTagIds = (tagGroups, contextRules, orgTagIdSet) => {
    const tagValues = getPathwayTagValues(tagGroups);
    if (!tagValues.length)
        return [];
    const matchingRules = contextRules.filter(contextRule => contextRule.rules.some(rule => arrayIncludesAny(tagValues, rule.tagIds)));
    return filterDuplicates(matchingRules
        .flatMap(contextRule => contextRule.rules.flatMap(rule => rule.tagIds))
        .filter(tagId => orgTagIdSet.has(tagId)));
};
/**
 * Detects violations of the compound uniqueness of (disease HV, organization) across pathways.
 * Pure function: performs no I/O, so it can be reused by backend (authoritative) and frontend (early UX).
 */
export const detectHVOrgConflicts = (input) => {
    const { pathway, candidatePathways, contextRules, orgTagIds, tagLabelById, variableNameById } = input;
    // An archived pathway is out of use: it is not checked and does not raise a conflict.
    // A conflict only reappears once it is un-archived (back to a normal, active pathway).
    if (pathway.archived)
        return [];
    if (!isDiseasePathway(pathway.type))
        return [];
    const variableIds = filterDuplicates(pathway.variableIds ?? []);
    if (!variableIds.length)
        return [];
    const orgTagIdSet = new Set(orgTagIds);
    const targetOrgTagIds = getPathwayOrgTagIds(pathway.tagGroups, contextRules, orgTagIdSet);
    if (!targetOrgTagIds.length)
        return [];
    // A candidate can conflict only if it's a different, non-archived disease pathway.
    // Archived candidates are ignored, so an active pathway does not clash with an archived twin.
    const relevantCandidates = candidatePathways.filter(candidate => candidate._id !== pathway._id && !candidate.archived && isDiseasePathway(candidate.type));
    const conflicts = [];
    for (const candidate of relevantCandidates) {
        // Validate for ALL shared disease HVs, not just one.
        const sharedHVs = variableIds.filter(id => (candidate.variableIds ?? []).includes(id));
        if (!sharedHVs.length)
            continue;
        const candidateOrgTagIds = getPathwayOrgTagIds(candidate.tagGroups, contextRules, orgTagIdSet);
        const sharedOrgTagIds = targetOrgTagIds.filter(id => candidateOrgTagIds.includes(id));
        if (!sharedOrgTagIds.length)
            continue;
        const orgNames = sharedOrgTagIds.map(id => tagLabelById[id] ?? id);
        for (const hvId of sharedHVs) {
            conflicts.push({
                hvId,
                hvName: variableNameById[hvId] ?? hvId,
                orgTagIds: sharedOrgTagIds,
                orgNames,
                pathwayId: candidate._id,
                pathwayLabel: candidate.label,
            });
        }
    }
    return conflicts;
};
// Group by (HV, Organization) so the sentence is stated once and the conflicting pathways are listed.
export const groupHVOrgConflicts = (conflicts) => {
    const groups = [];
    for (const conflict of conflicts) {
        const orgNames = filterDuplicates(conflict.orgNames);
        const key = `${conflict.hvName}::${orgNames.join(', ')}`;
        let group = groups.find(existing => `${existing.hvName}::${existing.orgNames.join(', ')}` === key);
        if (!group) {
            group = { hvName: conflict.hvName, orgNames, pathways: [] };
            groups.push(group);
        }
        if (!group.pathways.some(pathway => pathway.pathwayId === conflict.pathwayId))
            group.pathways.push({ pathwayId: conflict.pathwayId, pathwayLabel: conflict.pathwayLabel });
    }
    return groups;
};
export const formatHVOrgConflictMessage = (conflicts) => {
    return groupHVOrgConflicts(conflicts).map(group => {
        const header = `HV ${group.hvName} and Organization ${group.orgNames.join(', ')} already exists in these pathways:`;
        const pathwayLines = group.pathways.map(pathway => `• ${pathway.pathwayLabel} - ${pathway.pathwayId}`);
        return [header, ...pathwayLines].join('\n');
    }).join('\n\n');
};
//# sourceMappingURL=hv-org-uniqueness.js.map